import { useEffect } from "react"

import { useQuery } from "@tanstack/react-query"

import { DateFilter } from "@/types/filters"

import { supabase } from "../lib/supabase"

export interface ApplicationsPerDayDataPoint {
  date: string
  count: number
}

/**
 * Hook to fetch applications per day data
 * @param dateFilter - Optional date filter
 * @returns Query result with applications per day data
 */
export const useApplicationsPerDayData = (dateFilter?: DateFilter, jobId?: string) => {
  const query = useQuery<ApplicationsPerDayDataPoint[]>({
    queryKey: ["applicationsPerDay", dateFilter, jobId ?? "all"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("metrics-applications-per-day", {
        body: { dateFilter, jobId: jobId ?? null },
      })

      if (error) {
        throw new Error(`Error fetching applications per day data: ${error.message}`)
      }

      return data
    },
    // Keep data fresh for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Enable refetch on window focus
    refetchOnWindowFocus: true,
  })

  // Effect to refetch data when component mounts/tab becomes active
  useEffect(() => {
    // Refetch data when component mounts
    query.refetch()

    // Set up event listener for visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        query.refetch()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Clean up event listener
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [query])

  return query
}
